import { useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import type { History } from 'history'
import qs from 'qs'

export interface IQueryParams {
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

export function saveParamsToQuery(history: History, params: IQueryParams) {
  const currentQuery = qs.parse(window.location.search.substring(1))

  history.push({
    search: qs.stringify({
      ...currentQuery,
      ...params,
    }),
  })
}

export function resetParamsToQuery(history: History, params: IQueryParams) {
  history.push({
    search: qs.stringify(params),
  })
}

export interface IParamManipulationOptions {
  replace?: boolean
}

interface IUseQueryParams {
  queryParams: IQueryParams
  saveParams: (params: IQueryParams) => void
  resetParams: (params: IQueryParams) => void
  removeQueryParams: () => void
  removeSpecificParams: (paramNames: string | string[], options?: IParamManipulationOptions) => void
}

export function removeParams(history: History, paramsToRemove: string | string[], options?: IParamManipulationOptions) {
  const currentQuery = qs.parse(window.location.search.substring(1))

  const params = Array.isArray(paramsToRemove) ? paramsToRemove : [paramsToRemove]

  params.forEach(param => delete currentQuery[param])

  const handledParams = qs.stringify(currentQuery)

  if (options?.replace) history.replace({ search: handledParams })
  else history.push({ search: handledParams })
}

export function useQueryParams(): IUseQueryParams {
  const { search } = useLocation()
  const queryParams = qs.parse(search.substring(1))
  const history = useHistory()
  const saveParams = useCallback((params: IQueryParams) => saveParamsToQuery(history, params), [history])
  const resetParams = useCallback((params: IQueryParams) => resetParamsToQuery(history, params), [history])

  const removeQueryParams = useCallback(() => {
    history.push({
      pathname: history.location.pathname,
    })
  }, [history])

  const removeSpecificParams = useCallback(
    (paramNames: string | string[], options?: IParamManipulationOptions) => removeParams(history, paramNames, options),
    [history],
  )

  return { queryParams, saveParams, removeQueryParams, resetParams, removeSpecificParams }
}
